import { PropsWithChildren, useContext, useEffect, useState } from "react"
import { PublicClientApplication } from "@azure/msal-browser"
import { MsalProvider as MsalProviderBase } from "@azure/msal-react"

import { AuthContext } from "context/AuthContext"
import { ClientContext } from "context/ClientContext"

import { createMsalConfig } from "utilities/msalConfig"

export const MsalProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { client } = useContext(ClientContext)
  const { setAdb2cToken, setToken } = useContext(AuthContext)
  const [msalInstance, setMsalInstance] =
    useState<PublicClientApplication | null>(null)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!client) {
      return
    }

    const config = createMsalConfig(client)
    const instance = new PublicClientApplication(config)
    
    setMsalInstance(instance)
    setError(null)

    let isMounted = true

    const initializeMsal = async () => {
      try {
        await instance.initialize()
        try {
          const response = await instance.handleRedirectPromise()
          
          if (response?.account) {
            instance.setActiveAccount(response.account)
            
            if (response.idToken && isMounted) {
              setToken(undefined)
              setAdb2cToken(response.idToken)
            }
          }
        } catch (redirectError) {
          if (
            redirectError instanceof Error &&
            !redirectError.message.includes("interaction_in_progress")
          ) {
            console.error("[MSAL] Redirect processing error:", redirectError)
          }
        }

        if (!isMounted) {
          return
        }
      } catch (err) {
        if (isMounted) {
          console.error("[MSAL] Failed to initialize:", err)
          setError(err instanceof Error ? err : new Error(String(err)))
        }
      }
    }

    initializeMsal()

    return () => {
      isMounted = false
    }
  }, [client, setAdb2cToken, setToken])

  if (!client) {
    return children
  }

  if (error) {
    console.error("[MSAL] Provider error:", error)
  }

  if (!msalInstance) {
    return null
  }

  return <MsalProviderBase instance={msalInstance}>{children}</MsalProviderBase>
}
